import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, AlertCircle, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
export function AccessibilityPolicy() {
  const currentFeatures = [{
    category: 'Visual Accessibility',
    items: ['High contrast color schemes with WCAG AA compliance', 'Readable font sizes (minimum 16px body text)', 'Clear visual hierarchy and spacing', 'No reliance on color alone to convey information', 'Resizable text up to 200% without loss of functionality']
  }, {
    category: 'Navigation & Interaction',
    items: ['Keyboard navigation for all interactive elements', 'Skip navigation links on all pages', 'Consistent navigation structure across the site', 'Clear focus indicators for keyboard users', 'No time limits on interactions']
  }, {
    category: 'Content Accessibility',
    items: ['Semantic HTML structure', 'Descriptive link text (no "click here")', 'Alt text for all meaningful images', 'Captions and transcripts for video content', 'Plain language in all policies and content']
  }, {
    category: 'Screen Reader Support',
    items: ['ARIA labels and landmarks', 'Proper heading hierarchy (H1-H6)', 'Form labels and error messages', 'Skip to main content functionality', 'Tested with NVDA and JAWS']
  }];
  const inProgress = ['Automated accessibility testing in our development pipeline', 'User testing with disabled fat people', 'Improved mobile screen reader experience', 'Audio descriptions for complex visual content', 'Customizable color themes for different visual needs'];
  return <div className="min-h-screen bg-brand-cream" data-id="element-190">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-100 py-6 sticky top-16 z-40" data-id="element-191">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-192">
          <Link to="/policies" className="inline-flex items-center text-sm font-bold text-brand-navy hover:text-brand-lime transition-colors" data-id="element-193">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-194" />
            Back to Policies
          </Link>
        </div>
      </header>

      {/* Content */}
      <article className="py-16 px-4 sm:px-6 lg:px-8" data-id="element-195">
        <div className="max-w-4xl mx-auto" data-id="element-196">
          {/* Title */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-12" data-id="element-197">
            <span className="inline-block px-3 py-1 bg-brand-lime text-brand-navy text-xs font-bold uppercase tracking-wider mb-6" data-id="element-198">
              Accessibility Statement
            </span>

            <h1 className="text-4xl md:text-5xl font-black leading-tight mb-6 text-brand-black" data-id="element-199">
              Accessibility Statement
            </h1>

            <p className="text-lg text-gray-600 mb-4" data-id="element-200">
              Last updated: January 15, 2024
            </p>

            <div className="prose prose-lg" data-id="element-201">
              <p className="text-xl text-gray-700 leading-relaxed" data-id="element-202">
                ReGrowth is committed to ensuring digital accessibility for
                people with disabilities, with particular attention to the needs
                of fat disabled people who face compounded barriers.
              </p>
            </div>
          </motion.div>

          {/* Our Commitment */}
          <section className="mb-16" data-id="element-203">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-204">
              Our Commitment
            </h2>
            <div className="prose prose-lg max-w-none" data-id="element-205">
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-206">
                We are actively working to increase the accessibility and
                usability of our website, courses, and content. We strive to
                meet or exceed the requirements of the{' '}
                <strong data-id="element-207">
                  Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
                </strong>
                .
              </p>
              <p className="text-gray-700 leading-relaxed" data-id="element-208">
                Accessibility is not a one-time project—it's an ongoing
                commitment. We continuously test, evaluate, and improve our
                digital spaces based on feedback from our community.
              </p>
            </div>
          </section>

          {/* Current Features */}
          <section className="mb-16" data-id="element-209">
            <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-210">
              Current Accessibility Features
            </h2>

            <div className="space-y-8" data-id="element-211">
              {currentFeatures.map((section, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white border-2 border-brand-black p-6" data-id="element-212">
                  <h3 className="text-xl font-bold mb-4 text-brand-navy" data-id="element-213">
                    {section.category}
                  </h3>
                  <ul className="space-y-3" data-id="element-214">
                    {section.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-215">
                        <Check className="h-5 w-5 text-brand-lime mr-3 flex-shrink-0 mt-0.5" data-id="element-216" />
                        <span className="text-gray-700" data-id="element-217">{item}</span>
                      </li>)}
                  </ul>
                </motion.div>)}
            </div>
          </section>

          {/* In Progress */}
          <section className="mb-16" data-id="element-218">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-219">
              Work in Progress
            </h2>
            <div className="bg-brand-lime/10 border-l-4 border-brand-lime p-6 mb-6" data-id="element-220">
              <p className="text-gray-700 mb-4" data-id="element-221">
                We're actively working on the following improvements:
              </p>
              <ul className="space-y-3" data-id="element-222">
                {inProgress.map((item, i) => <li key={i} className="flex items-start" data-id="element-223">
                    <AlertCircle className="h-5 w-5 text-brand-lime mr-3 flex-shrink-0 mt-0.5" data-id="element-224" />
                    <span className="text-gray-700" data-id="element-225">{item}</span>
                  </li>)}
              </ul>
            </div>
            <p className="text-sm text-gray-600" data-id="element-226">
              Expected completion: Q2 2024. We'll update this page as features
              are implemented.
            </p>
          </section>

          {/* Known Issues */}
          <section className="mb-16" data-id="element-227">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-228">
              Known Issues
            </h2>
            <div className="bg-gray-50 border-2 border-gray-200 p-6" data-id="element-229">
              <p className="text-gray-700 mb-4" data-id="element-230">
                We're aware of the following accessibility barriers and are
                working to address them:
              </p>
              <ul className="space-y-3 text-gray-700" data-id="element-231">
                <li className="flex items-start" data-id="element-232">
                  <span className="mr-3" data-id="element-233">•</span>
                  <span data-id="element-234">
                    Some older blog posts may not have complete alt text for
                    images
                  </span>
                </li>
                <li className="flex items-start" data-id="element-235">
                  <span className="mr-3" data-id="element-236">•</span>
                  <span data-id="element-237">
                    Video content from 2023 may not have captions (we're
                    retroactively adding them)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-238">
                  <span className="mr-3" data-id="element-239">•</span>
                  <span data-id="element-240">
                    Some third-party embedded content (social media, videos) may
                    not meet our accessibility standards
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Assistive Technologies */}
          <section className="mb-16" data-id="element-241">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-242">
              Assistive Technologies
            </h2>
            <div className="prose prose-lg max-w-none" data-id="element-243">
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-244">
                ReGrowth is designed to be compatible with the following
                assistive technologies:
              </p>
              <ul className="text-gray-700 space-y-2" data-id="element-245">
                <li data-id="element-246">Screen readers (NVDA, JAWS, VoiceOver, TalkBack)</li>
                <li data-id="element-247">Screen magnification software</li>
                <li data-id="element-248">Speech recognition software</li>
                <li data-id="element-249">Keyboard-only navigation</li>
                <li data-id="element-250">Switch devices</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4" data-id="element-251">
                We test our site with the latest versions of major browsers
                (Chrome, Firefox, Safari, Edge) and screen readers.
              </p>
            </div>
          </section>

          {/* Course Accessibility */}
          <section className="mb-16" data-id="element-252">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-253">
              Course & Event Accessibility
            </h2>
            <div className="prose prose-lg max-w-none" data-id="element-254">
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-255">
                For our live courses and events, we provide:
              </p>
              <ul className="text-gray-700 space-y-2 mb-6" data-id="element-256">
                <li data-id="element-257">
                  <strong data-id="element-258">Live captions</strong> for all video sessions
                </li>
                <li data-id="element-259">
                  <strong data-id="element-260">Recordings</strong> available within 24 hours with
                  captions and transcripts
                </li>
                <li data-id="element-261">
                  <strong data-id="element-262">Alternative formats</strong> for course materials
                  (PDF, Word, plain text)
                </li>
                <li data-id="element-263">
                  <strong data-id="element-264">Flexible participation</strong> options (video on/off,
                  chat vs. voice)
                </li>
                <li data-id="element-265">
                  <strong data-id="element-266">Break times</strong> built into all sessions
                </li>
                <li data-id="element-267">
                  <strong data-id="element-268">Accommodations</strong> available upon request (ASL
                  interpretation, extended time, etc.)
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed" data-id="element-269">
                If you need specific accommodations for a course or event,
                please contact us at least 2 weeks in advance at{' '}
                <a href="mailto:access@regrowth.com" className="text-brand-navy font-bold hover:underline" data-id="element-270">
                  access@regrowth.com
                </a>
                .
              </p>
            </div>
          </section>

          {/* Feedback */}
          <section className="mb-16" data-id="element-271">
            <div className="bg-brand-navy text-white p-8 border-2 border-brand-black" data-id="element-272">
              <div className="flex items-start gap-4" data-id="element-273">
                <Mail className="h-8 w-8 flex-shrink-0 mt-1" data-id="element-274" />
                <div data-id="element-275">
                  <h2 className="text-2xl font-black uppercase mb-4" data-id="element-276">
                    Feedback & Support
                  </h2>
                  <p className="text-gray-200 mb-4 leading-relaxed" data-id="element-277">
                    We welcome your feedback on the accessibility of ReGrowth.
                    If you encounter accessibility barriers, please let us know:
                  </p>
                  <ul className="space-y-2 text-gray-200 mb-6" data-id="element-278">
                    <li data-id="element-279">
                      <strong data-id="element-280">Email:</strong>{' '}
                      <a href="mailto:access@regrowth.com" className="text-brand-lime hover:underline" data-id="element-281">
                        access@regrowth.com
                      </a>
                    </li>
                    <li data-id="element-282">
                      <strong data-id="element-283">Response time:</strong> We aim to respond within 2
                      business days
                    </li>
                    <li data-id="element-284">
                      <strong data-id="element-285">What to include:</strong> URL of the page,
                      description of the issue, assistive technology you're
                      using
                    </li>
                  </ul>
                  <p className="text-sm text-gray-300" data-id="element-286">
                    We take accessibility feedback seriously and will work with
                    you to provide the information or service you need in an
                    accessible format.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Standards */}
          <section className="mb-16" data-id="element-287">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-288">
              Technical Standards
            </h2>
            <div className="prose prose-lg max-w-none" data-id="element-289">
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-290">
                ReGrowth aims to conform to the following standards:
              </p>
              <ul className="text-gray-700 space-y-2" data-id="element-291">
                <li data-id="element-292">
                  <strong data-id="element-293">WCAG 2.1 Level AA</strong> - Web Content Accessibility
                  Guidelines
                </li>
                <li data-id="element-294">
                  <strong data-id="element-295">Section 508</strong> - U.S. federal accessibility
                  requirements
                </li>
                <li data-id="element-296">
                  <strong data-id="element-297">ARIA</strong> - Accessible Rich Internet Applications
                  specifications
                </li>
              </ul>
            </div>
          </section>

          {/* Limitations */}
          <section className="mb-16" data-id="element-298">
            <h2 className="text-3xl font-black uppercase mb-6 text-brand-black" data-id="element-299">
              Limitations & Alternatives
            </h2>
            <div className="prose prose-lg max-w-none" data-id="element-300">
              <p className="text-gray-700 leading-relaxed mb-4" data-id="element-301">
                Despite our best efforts, some content may not be fully
                accessible. When we identify limitations, we provide
                alternatives:
              </p>
              <ul className="text-gray-700 space-y-2" data-id="element-302">
                <li data-id="element-303">If a video lacks captions, we provide a full transcript</li>
                <li data-id="element-304">
                  If an image is complex, we provide a detailed text description
                </li>
                <li data-id="element-305">
                  If a PDF is not accessible, we provide an HTML or Word version
                </li>
                <li data-id="element-306">
                  If live participation is not possible, we provide recordings
                  and async options
                </li>
              </ul>
            </div>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t-2 border-gray-200" data-id="element-307">
            <p className="text-sm text-gray-600" data-id="element-308">
              This accessibility statement was last reviewed and updated on
              January 15, 2024. We review and update this statement quarterly.
            </p>
          </div>
        </div>
      </article>
    </div>;
}